import React, { useState } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Paper,
  TextField,
  Slider,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Checkbox,
  Grid,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search";
import SubPageLayout from "../../components/shared/SubPageLayout";

import logger from '../../utils/logger';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2),
  maxWidth: 900,
  marginLeft: "auto",
  marginRight: "auto",
}));

const AccountLookup = ({
  searchText,
  setSearchText,
  formData,
  setFormData,
  sampleAccounts,
}) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Account Lookup
      </Typography>
      <TextField
        fullWidth
        placeholder="Search by account number or name"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{
          mb: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "28px",
            "& fieldset": {
              borderColor: "rgba(0, 0, 0, 0.12)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(0, 0, 0, 0.24)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "primary.main",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
          "& .MuiInputBase-input": {
            "&:focus": {
              boxShadow: "none",
              outline: "none",
            },
          },
          "& .MuiInputAdornment-root": {
            ml: 1.5,
            mr: 0.5,
            "& .MuiSvgIcon-root": {
              fontSize: "1.2rem",
            },
          },
          backgroundColor: "#f5f5f5",
          borderRadius: "28px",
          "&:hover": {
            backgroundColor: "#f0f0f0",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Sample accounts list */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom>
          Available Accounts
        </Typography>
        {sampleAccounts.map((account) => (
          <Box
            key={account.accountNumber}
            sx={{
              p: 2,
              mb: 1,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              cursor: "pointer",
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            }}
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                accountDetails: {
                  ...prev.accountDetails,
                  ...account,
                },
              }))
            }
          >
            <Typography variant="subtitle1">
              {account.type} - {account.accountNumber}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {account.bankName}
            </Typography>
          </Box>
        ))}
      </Box>

      {formData.accountDetails && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Selected Account Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Account Information
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.accountDetails.type} -{" "}
                {formData.accountDetails.accountNumber}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {formData.accountDetails.accountName}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {formData.accountDetails.bankName}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="textSecondary">
                Available cash
                <Tooltip title="Current available cash balance">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="h6">
                ${formData.accountDetails.availableCash.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="textSecondary">
                Unsettled cash
              </Typography>
              <Typography variant="h6">
                ${formData.accountDetails.unsettledCash.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="textSecondary">
                Total market value
              </Typography>
              <Typography variant="h6">
                ${formData.accountDetails.totalMarketValue.toFixed(2)}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Tax withholding elections
            </Typography>
            <Typography>
              Federal: {formData.accountDetails.federalTax}% State (IL):{" "}
              {formData.accountDetails.stateTax}%
              <Button variant="text" size="small" sx={{ ml: 1 }}>
                Edit
              </Button>
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const WithdrawalsDistributions = () => {
  const [currentPhase, setCurrentPhase] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [formData, setFormData] = useState({
    selectedAccount: "",
    accountDetails: {
      type: "Traditional IRA",
      accountNumber: "7935407394",
      accountName: "CHARLES SCHWAB & CO INC CUST",
      bankName: "FIFTH THIRD BANK NA",
      bankAccountNumber: "7935407394",
      routingNumber: "042000314",
      availableCash: 2614.48,
      unsettledCash: 0.0,
      totalMarketValue: 140292.27,
      federalTax: 12,
      stateTax: 0,
    },
    transactionType: "ACH",
    transferDirection: "outgoing",
    completionDate: "",
    amount: "",
    isGross: true,
    distributionReason: "BENEFICIARY (4)",
    acknowledgement: false,
  });

  // Sample accounts data
  const sampleAccounts = [
    {
      type: "Traditional IRA",
      accountNumber: "7935407394",
      accountName: "CHARLES SCHWAB & CO INC CUST",
      bankName: "FIFTH THIRD BANK NA",
      bankAccountNumber: "7935407394",
      routingNumber: "042000314",
    },
    {
      type: "Roth IRA",
      accountNumber: "7935407395",
      accountName: "CHARLES SCHWAB & CO INC CUST",
      bankName: "FIFTH THIRD BANK NA",
      bankAccountNumber: "7935407395",
      routingNumber: "042000314",
    },
    {
      type: "Individual Account",
      accountNumber: "7935407396",
      accountName: "CHARLES SCHWAB & CO INC CUST",
      bankName: "FIFTH THIRD BANK NA",
      bankAccountNumber: "7935407396",
      routingNumber: "042000314",
    },
  ];

  const steps = [
    "Select Account",
    "Create Transaction",
    "Transaction Details",
    "Review",
  ];

  const handleChange = (field) => (event) => {
    const value = event.target ? event.target.value : event;
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleNext = () => {
    setCurrentPhase((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentPhase((prev) => prev - 1);
  };

  const handleSubmit = () => {
    // Handle form submission
    logger.info("Form submitted:", formData);
  };

  const Phase1 = () => (
    <Box>
      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend">Transaction type</FormLabel>
        <RadioGroup
          value={formData.transactionType}
          onChange={handleChange("transactionType")}
        >
          <FormControlLabel
            value="ACH"
            control={<Radio />}
            label="ACH (MoneyLink)"
          />
          <FormControlLabel
            value="internal"
            control={<Radio />}
            label="Internal transfer (journal)"
          />
          <FormControlLabel value="check" control={<Radio />} label="Check" />
          <FormControlLabel value="wire" control={<Radio />} label="Wire" />
        </RadioGroup>
      </FormControl>

      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend">Transfer direction</FormLabel>
        <RadioGroup
          value={formData.transferDirection}
          onChange={handleChange("transferDirection")}
        >
          <FormControlLabel
            value="outgoing"
            control={<Radio />}
            label="Outgoing"
          />
          <FormControlLabel
            value="incoming"
            control={<Radio />}
            label="Incoming"
          />
        </RadioGroup>
      </FormControl>

      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend">Account to receive funds</FormLabel>
        <RadioGroup
          value={formData.selectedAccount}
          onChange={handleChange("selectedAccount")}
        >
          {sampleAccounts.map((account) => (
            <FormControlLabel
              key={account.accountNumber}
              value={account.accountNumber}
              control={<Radio />}
              label={`${account.bankName} (Account ending in ${account.accountNumber.slice(-4)})`}
            />
          ))}
        </RadioGroup>
        <Button variant="text" size="small" sx={{ mt: 1 }}>
          Add or manage accounts
        </Button>
      </FormControl>
    </Box>
  );

  const Phase2 = () => (
    <Box>
      <TextField
        fullWidth
        label="Completion Date"
        type="date"
        value={formData.completionDate}
        onChange={handleChange("completionDate")}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{ mb: 3 }}
      />

      <TextField
        fullWidth
        label="Amount"
        type="text"
        inputMode="decimal"
        value={formData.amount}
        onChange={(e) => {
          const value = e.target.value.replace(/[^0-9.]/g, "");
          if (value === "" || /^\d*\.?\d*$/.test(value)) {
            setFormData((prev) => ({ ...prev, amount: value }));
          }
        }}
        helperText="Maximum amount: $500,000.00"
        sx={{ mb: 3 }}
      />

      <FormControl fullWidth sx={{ mb: 3 }}>
        <FormLabel>Amount Type</FormLabel>
        <RadioGroup
          value={formData.isGross ? "gross" : "net"}
          onChange={(e) =>
            setFormData({ ...formData, isGross: e.target.value === "gross" })
          }
        >
          <FormControlLabel value="gross" control={<Radio />} label="Gross" />
          <FormControlLabel value="net" control={<Radio />} label="Net" />
        </RadioGroup>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <FormLabel>
          Federal Tax ({formData.accountDetails.federalTax}%)
        </FormLabel>
        <Slider
          value={formData.accountDetails.federalTax}
          onChange={(e, newValue) =>
            setFormData({
              ...formData,
              accountDetails: {
                ...formData.accountDetails,
                federalTax: newValue,
              },
            })
          }
          min={0}
          max={100}
          valueLabelDisplay="auto"
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <FormLabel>State Tax ({formData.accountDetails.stateTax}%)</FormLabel>
        <Slider
          value={formData.accountDetails.stateTax}
          onChange={(e, newValue) =>
            setFormData({
              ...formData,
              accountDetails: {
                ...formData.accountDetails,
                stateTax: newValue,
              },
            })
          }
          min={0}
          max={100}
          valueLabelDisplay="auto"
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <FormLabel>Distribution Reason</FormLabel>
        <Select
          value={formData.distributionReason}
          onChange={handleChange("distributionReason")}
        >
          <MenuItem value="BENEFICIARY (4)">BENEFICIARY (4)</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  const Phase3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Account Details
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Account Type: {formData.accountDetails.type}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Account Number: {formData.accountDetails.accountNumber}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Account Name: {formData.accountDetails.accountName}
          </Typography>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        Transaction Summary
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <Typography variant="body2" color="textSecondary">
            Amount:
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>${formData.amount}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" color="textSecondary">
            Federal Tax ({formData.accountDetails.federalTax}%):
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            $
            {(
              (formData.amount * formData.accountDetails.federalTax) /
              100
            ).toFixed(2)}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" color="textSecondary">
            State Tax ({formData.accountDetails.stateTax}%):
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            $
            {(
              (formData.amount * formData.accountDetails.stateTax) /
              100
            ).toFixed(2)}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" color="textSecondary">
            Net Amount:
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>
            $
            {(
              formData.amount *
              (1 -
                (formData.accountDetails.federalTax +
                  formData.accountDetails.stateTax) /
                  100)
            ).toFixed(2)}
          </Typography>
        </Grid>
      </Grid>

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.acknowledgement}
            onChange={(e) =>
              setFormData({ ...formData, acknowledgement: e.target.checked })
            }
          />
        }
        label="I confirm that the client has reviewed and authorized this instruction"
        sx={{ mb: 3 }}
      />
    </Box>
  );

  const breadcrumbItems = [
    { label: "Portfolio Management", link: "/portfolio-management" },
    {
      label: "Withdrawals & Distributions",
      link: "/portfolio-management/withdrawals-distributions",
    },
  ];

  return (
    <SubPageLayout
      title="Withdrawals & Distributions"
      subtitle="Manage client withdrawals and distributions"
      breadcrumbItems={breadcrumbItems}
    >
      <Box>
        <Stepper activeStep={currentPhase - 1} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <StyledPaper>
          {currentPhase === 1 && (
            <AccountLookup
              searchText={searchText}
              setSearchText={setSearchText}
              formData={formData}
              setFormData={setFormData}
              sampleAccounts={sampleAccounts}
            />
          )}
          {currentPhase === 2 && <Phase1 />}
          {currentPhase === 3 && <Phase2 />}
          {currentPhase === 4 && <Phase3 />}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            {currentPhase > 1 && (
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            )}
            {currentPhase < 4 ? (
              <Button variant="contained" onClick={handleNext}>
                Continue
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!formData.acknowledgement}
              >
                Submit
              </Button>
            )}
          </Box>
        </StyledPaper>
      </Box>
    </SubPageLayout>
  );
};

export default WithdrawalsDistributions;
